import { useQuery } from '@tanstack/react-query'
import { createContext, createElement, type ReactNode, useCallback, useContext, useMemo } from 'react'

import type { ESKResponse, GetContentModulesResponse, GetSidekicksResponse } from '@/services/api/api.types'
import { deduplicateByName } from '@/utils/sidekicks'

import { useGlobalState } from './useGlobalState'

export type ESKWithId = ESKResponse & { eskId: string; fromSponsor?: boolean }

export type AllEsksValue = {
  allEsks: ESKWithId[]
  isReady: boolean
  isLoading: boolean
  isFetching: boolean
  getEskName: (eskId: string | null | undefined) => string | null
  getEsk: (eskId: string | null | undefined) => ESKWithId | undefined
  sidekicksQuery: ReturnType<typeof useQuery<GetSidekicksResponse, Error>>
  sidekickPoolQuery: ReturnType<typeof useQuery<GetSidekicksResponse | null, Error>>
}

const AllEsksContext = createContext<AllEsksValue | undefined>(undefined)

/**
 * Internal hook that runs the sidekick queries and derived state.
 * Used only by AllEsksProvider so there is a single subscription.
 */
function useAllEsksQueries(): AllEsksValue {
  const { api } = useGlobalState()

  const sidekicksQuery = useQuery({
    queryKey: ['sponsor', 'sidekicks'],
    queryFn: api.getSidekicks,
    initialData: {
      mskTypes: [],
      eskTypes: [],
    },
    refetchOnWindowFocus: false,
  })

  const sidekickPoolQuery = useQuery({
    queryKey: ['sponsor', 'sidekick-pool'],
    queryFn: api.getSidekickPool,
    initialData: null,
    refetchOnWindowFocus: false,
  })

  const contentModulesQuery = useQuery({
    queryKey: ['sponsor', 'content-modules'],
    queryFn: () => api.getContentModules(),
    initialData: [] as GetContentModulesResponse,
    refetchOnWindowFocus: false,
  })

  const isReady = !sidekicksQuery.isFetching && !sidekickPoolQuery.isFetching

  const allEsks = useMemo((): ESKWithId[] => {
    const sponsorEsks = sidekicksQuery.data?.eskTypes ?? []
    const poolEsks = sidekickPoolQuery.data?.eskTypes ?? []
    const mskNames = new Set((sidekicksQuery.data?.mskTypes ?? []).map(msk => msk.name))

    // Build set of ESK types that are actively assigned to content modules
    const cmAssignedEskTypes = new Set(
      (contentModulesQuery.data ?? []).map(cm => cm.selectEsk).filter((v): v is string => v != null),
    )

    const sponsorEskTypes = new Set(sponsorEsks.map(esk => esk.type))

    const sponsorEskOptions: ESKWithId[] = sponsorEsks
      .filter(esk => !mskNames.has(esk.name))
      .map(esk => ({
        ...esk,
        eskId: esk.type,
        fromSponsor: true,
      }))

    const poolEskOptions: ESKWithId[] = poolEsks
      .filter(esk => !sponsorEskTypes.has(esk.type) && !mskNames.has(esk.name))
      .map(esk => ({
        ...esk,
        eskId: esk.type,
      }))

    const combined = [...sponsorEskOptions, ...poolEskOptions]
    const uniqueByType = new Map<string, ESKWithId>()
    for (const esk of combined) {
      if (uniqueByType.has(esk.eskId)) {
        console.warn(`[ESK] Duplicate ESK detected: ${esk.eskId} (${esk.name})`)
      } else {
        uniqueByType.set(esk.eskId, esk)
      }
    }
    const byTypeList = Array.from(uniqueByType.values())

    return deduplicateByName(byTypeList, {
      getName: e => e.name ?? '',
      getId: e => e.eskId,
      // Priority: CM-assigned (2) > fromSponsor only (1) > neither (0)
      getPriority: e => (e.fromSponsor ? 1 : 0) + (cmAssignedEskTypes.has(e.eskId) ? 2 : 0),
      idLabel: 'eskId',
      logPrefix: '[ESK] ',
    })
  }, [sidekicksQuery.data, sidekickPoolQuery.data, contentModulesQuery.data])

  const getEskName = useCallback(
    (eskId: string | null | undefined): string | null => {
      if (!eskId) return null
      if (eskId === 'PSK') return 'Personal Sidekick'
      if (!isReady) return null
      return allEsks.find(e => e.eskId === eskId)?.name ?? null
    },
    [allEsks, isReady],
  )

  const getEsk = useCallback(
    (eskId: string | null | undefined): ESKWithId | undefined => {
      if (!eskId) return undefined
      return allEsks.find(e => e.eskId === eskId)
    },
    [allEsks],
  )

  const isLoading = sidekicksQuery.isLoading || sidekickPoolQuery.isLoading
  const isFetching = sidekicksQuery.isFetching || sidekickPoolQuery.isFetching

  // Field-level deps, not the query objects: useQuery returns a fresh result object every render.
  return useMemo(
    () => ({
      allEsks,
      isReady,
      isLoading,
      isFetching,
      getEskName,
      getEsk,
      sidekicksQuery,
      sidekickPoolQuery,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers take the query objects whole, so they can't be narrowed here
    [
      allEsks,
      isReady,
      isLoading,
      isFetching,
      getEskName,
      getEsk,
      sidekicksQuery.data,
      sidekicksQuery.isSuccess,
      sidekickPoolQuery.data,
      sidekickPoolQuery.isSuccess,
    ],
  )
}

export function AllEsksProvider(props: { children: ReactNode }) {
  const value = useAllEsksQueries()
  return createElement(AllEsksContext.Provider, { value }, props.children)
}

/**
 * Hook that fetches and combines ESKs from both sponsor and pool sources.
 * Must be used within AllEsksProvider (e.g. under DashboardLayout).
 * Provides proper loading state tracking to prevent showing raw IDs before data is ready.
 */
export function useAllEsks(): AllEsksValue {
  const value = useContext(AllEsksContext)
  if (value === undefined) {
    throw new Error('useAllEsks must be used within AllEsksProvider')
  }
  return value
}
